"use client";

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  styled,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Upload as UploadIcon } from "@mui/icons-material";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(3),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
}));

const EditProviderDialog = ({ open, onClose, onSave, provider }) => {
  const [formData, setFormData] = useState({
    providerName: "",
    address: "",
    number: "",
    postcode: "",
    place: "",
    region: "",
    latitude: null,
    longitude: null,
    branch: "",
    telephone: "",
    mobile: "",
    email: "",
    domain: "",
    category: "",
    openTime: null,
    closeTime: null,
    orderStartDay: "",
    orderEndDay: "",
    deliveryCost: "",
    minOrderCost: "",
    deliveryCircle: "",
    deliveryApproxTime: "",
    description: "",
    Imprint: "",
    paypalMode: false,
    cashMode: false,
    Abholung: false,
    Lieferung: false,
    availability: false,
    deactivate: false,
    iswelcome: false,
    logo: null,
    extraImage: null,
    facebook: "",
    instagram: "",
    x: "",
    tiktok: "",
    whatsapp: "",
  });
  const [branchOptions, setBranchOptions] = useState([]);
  const [communityOptions, setCommunityOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [extraImagePreview, setExtraImagePreview] = useState(null);

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      try {
        // Fetch branches
        const branchResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/getcategory?type=Branch`,
          {
            headers: {
              Accept: "application/json",
            },
          }
        );

        // Fetch communities
        const communityResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/getcategory?type=Community`,
          {
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (!branchResponse.ok || !communityResponse.ok) {
          throw new Error("Failed to fetch options");
        }

        const branchData = await branchResponse.json();
        const communityData = await communityResponse.json();

        setBranchOptions(branchData.result);
        setCommunityOptions(communityData.result);
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Update form data when provider prop changes
  useEffect(() => {
    if (provider) {
      // Remove posts from provider
      const { posts, ...providerWithoutPosts } = provider;

      setFormData({
        ...providerWithoutPosts,
        deliveryCost: Number(provider.deliveryCost || 0),
        minOrderCost: Number(provider.minOrderCost || 0),
        latitude: provider.latitude || null,
        longitude: provider.longitude || null,
        paypalMode: Boolean(provider.paypalMode),
        paypalClientId: provider.paypalClientId || "",
        paypalClientSecret: provider.paypalClientSecret || "",
        cashMode: Boolean(provider.cashMode),
        Abholung: Boolean(provider.Abholung),
        Lieferung: Boolean(provider.Lieferung),
        availability: Boolean(provider.availability),
        deactivate: Boolean(provider.deactivate),
        iswelcome: Boolean(provider.iswelcome),
        facebook: provider.facebook || "",
        instagram: provider.instagram || "",
        x: provider.x || "",
        tiktok: provider.tiktok || "",
        whatsapp: provider.whatsapp || "",
      });

      if (provider.logo?.url.startsWith("http")) {
        setLogoPreview(provider.logo.url);
      } else {
        setLogoPreview(
          `https://www.mein-vendoapp.de:3001${provider.logo?.url}`
        );
      }

      if (provider.extraImage?.url.startsWith("http")) {
        setExtraImagePreview(provider.extraImage.url);
      } else {
        setExtraImagePreview(
          `https://www.mein-vendoapp.de:3001${provider.extraImage?.url}`
        );
      }
    }
  }, [provider]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
          ? Number(value)
          : value,
    }));
  };

  const handleTimeChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        logo: file,
      }));
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleLExtraChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        extraImage: file,
      }));
      setExtraImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Format time values back to string format for API
    const formattedData = {
      ...formData,
      deliveryCost: formData.deliveryCost.toString(),
      minOrderCost: formData.minOrderCost.toString(),
    };

    //console.log(JSON.stringify(formattedData));

    onSave(formattedData);
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Provider</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              {/* Logo upload section */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {logoPreview && (
                    <Box
                      component="img"
                      src={logoPreview}
                      alt="Provider logo"
                      sx={{
                        width: 200,
                        height: 200,
                        objectFit: "contain",
                        borderRadius: 1,
                      }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                  >
                    Upload Logo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLogoChange}
                    />
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  {extraImagePreview && (
                    <Box
                      component="img"
                      src={extraImagePreview}
                      alt="Extra Image"
                      sx={{
                        width: 200,
                        height: 200,
                        objectFit: "contain",
                        borderRadius: 1,
                      }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                  >
                    Upload ExtraImage
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleLExtraChange}
                    />
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Provider Name"
                  name="providerName"
                  value={formData.providerName}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Number"
                  name="number"
                  value={formData.number}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postcode"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Facebook"
                  name="facebook"
                  type="facebook"
                  value={formData.facebook}
                  onChange={handleChange}
                  
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram"
                  name="instagram"
                  type="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Whatsapp"
                  name="whatsapp"
                  type="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="X(Twitter)"
                  name="x"
                  type="x"
                  value={formData.x}
                  onChange={handleChange}
                  
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tiktok"
                  name="tiktok"
                  type="tiktok"
                  value={formData.tiktok}
                  onChange={handleChange}
                  
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  name="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude ?? ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  name="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude ?? ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Region"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="branch-label">Branch</InputLabel>
                  <Select
                    labelId="branch-label"
                    id="branch"
                    name="branch"
                    value={formData.branch || ""}
                    onChange={handleChange}
                    label="Branch"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {branchOptions?.length > 0 &&
                      branchOptions.map((branch) => (
                        <MenuItem key={branch.Name} value={branch.Name}>
                          {branch.Name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Domain"
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                />
              </Grid>
              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Order Start Time"
                  name="openTime"
                  value={formData.openTime}
                  onChange={handleChange}
                  placeholder="HH:MM"
                />
              </Grid> */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Delivery Cost"
                  name="deliveryCost"
                  type="number"
                  value={formData.deliveryCost}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Min Order Cost"
                  name="minOrderCost"
                  type="number"
                  value={formData.minOrderCost}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Delivery Circle"
                  name="deliveryCircle"
                  value={formData.deliveryCircle}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Delivery Approx Time"
                  name="deliveryApproxTime"
                  value={formData.deliveryApproxTime}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.paypalMode}
                      onChange={handleChange}
                      name="paypalMode"
                    />
                  }
                  label="Allow Paypal"
                />
              </Grid>
              {formData.paypalMode && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PayPal Client ID"
                      name="paypalClientId"
                      value={formData.paypalClientId}
                      onChange={handleChange}
                      required={formData.paypalMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="PayPal Client Secret"
                      name="paypalClientSecret"
                      value={formData.paypalClientSecret}
                      onChange={handleChange}
                      required={formData.paypalMode}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.cashMode}
                      onChange={handleChange}
                      name="cashMode"
                    />
                  }
                  label="Allow Cash"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Abholung}
                      onChange={handleChange}
                      name="Abholung"
                    />
                  }
                  label="Abholung"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Lieferung}
                      onChange={handleChange}
                      name="Lieferung"
                    />
                  }
                  label="Lieferung"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.availability}
                      onChange={handleChange}
                      name="availability"
                    />
                  }
                  label="Open"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.deactivate}
                      onChange={handleChange}
                      name="deactivate"
                    />
                  }
                  label="Deactivate"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.iswelcome}
                      onChange={handleChange}
                      name="iswelcome"
                    />
                  }
                  label="Welcome Page"
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default EditProviderDialog;
